import type { ApiSchemas } from '@rental/contracts/client'
import { FUEL_EIGHTHS } from '@rental/domain'
import { format, type StaffMessages } from '@rental/i18n'

/** Full first, as the gauge is read at the counter. */
export const FUEL_LEVELS = Array.from(
  { length: FUEL_EIGHTHS + 1 },
  (_, index) => FUEL_EIGHTHS - index,
)

export function fuelLabel(level: number, m: StaffMessages): string {
  if (level === FUEL_EIGHTHS) return m.rental.fuelFull
  if (level === 0) return m.rental.fuelEmpty
  return format(m.rental.fuelEighths, { level })
}

type HandOverSheet = Pick<ApiSchemas['CheckoutSheet'], 'contract' | 'termsBlock'>

/**
 * CTR-2: the contract signed on the iPad for this booking as it stands and
 * the car that leaves. The server decides; the screen mirrors it.
 */
export function signedOnIpad(sheet: Pick<HandOverSheet, 'contract'>, vehicleId: string): boolean {
  return sheet.contract.status === 'signed' && sheet.contract.vehicleId === vehicleId
}

/**
 * C1: why this hand-over cannot happen, as the server will answer it. A car
 * leaving on the iPad's signed contract carries that contract's terms; any
 * other leaves on the terms in force, and with none, or a DRAFT in
 * production, nobody can override it.
 */
export function termsStop(sheet: HandOverSheet, vehicleId: string): HandOverSheet['termsBlock'] {
  return signedOnIpad(sheet, vehicleId) ? null : sheet.termsBlock
}

/** Something the report to CaRGOS still waits for, in the form's order. */
export type CargosSendReason = 'paymentType' | 'contract' | 'portal'

interface CargosSendForm {
  paymentType: string
  contract: 'digital' | 'paper' | 'none'
  afterUnknown: boolean
  portalChecked: boolean
}

/**
 * CMP-1: what the counter must give before asking the app to report the
 * contract to CaRGOS, each said beside the button: a payment type from
 * CaRGOS's table, a contract signed (on the iPad, or the printed one ticked)
 * and, after an outcome nobody knows, the portal checked first (plan change
 * 2). The server checks each again.
 */
export function cargosSendReasons(form: CargosSendForm): CargosSendReason[] {
  const lacking: readonly (readonly [CargosSendReason, boolean])[] = [
    ['paymentType', form.paymentType === ''],
    ['contract', form.contract === 'none'],
    ['portal', form.afterUnknown && !form.portalChecked],
  ]
  return lacking.filter(([, missing]) => missing).map(([reason]) => reason)
}

/** Whether the counter may ask the app to report the contract to CaRGOS. */
export function cargosSendReady(form: CargosSendForm): boolean {
  return cargosSendReasons(form).length === 0
}
